import { coffeeRecipes, ingredients } from '../data/coffeeRecipes';

export class SequenceValidator {
  constructor(recipe) {
    this.recipe = coffeeRecipes[recipe];
    this.currentStep = 0;
    this.completedSteps = [];
    this.errors = [];
    this.startTime = Date.now();
    this.stepTimes = [];
  }

  validateStep(ingredientId) {
    const expectedStep = this.recipe.sequence[this.currentStep];
    const stepStartTime = Date.now();
    
    if (ingredientId === expectedStep) {
      // Correct step
      this.completedSteps.push({
        ingredient: ingredientId,
        stepNumber: this.currentStep,
        timestamp: stepStartTime,
        correct: true
      });
      
      this.stepTimes.push(stepStartTime - (this.stepTimes[this.stepTimes.length - 1]?.timestamp || this.startTime));
      this.currentStep++;
      
      return {
        success: true,
        message: `Perfect! ${ingredients[ingredientId]?.name} added correctly.`,
        nextStep: this.currentStep < this.recipe.sequence.length ? this.recipe.sequence[this.currentStep] : null,
        progress: this.currentStep / this.recipe.sequence.length,
        isComplete: this.currentStep >= this.recipe.sequence.length
      };
    } else {
      // Wrong step
      this.errors.push({
        expected: expectedStep,
        received: ingredientId,
        stepNumber: this.currentStep,
        timestamp: stepStartTime
      });
      
      return {
        success: false,
        message: `Wrong step! Expected ${ingredients[expectedStep]?.name}, got ${ingredients[ingredientId]?.name}.`,
        expectedStep: expectedStep,
        currentStep: this.currentStep,
        hint: this.getHint(expectedStep)
      };
    }
  }

  getHint(expectedStep) {
    const hints = {
      espresso: "Start with pulling the espresso shot - it's the foundation!",
      steamed_milk: "Steam the milk to the right temperature and texture.",
      milk_foam: "Create thick, creamy foam for the perfect cappuccino.",
      light_foam: "Add just a thin layer of light foam for a latte.",
      microfoam_milk: "Create silky microfoam for a smooth flat white.",
      hot_water: "Add hot water to dilute the espresso.",
      matcha_powder: "Start with measuring the matcha powder.",
      whisk: "Whisk the matcha until it's frothy and well mixed.",
      ice: "Always add ice first for cold drinks.",
      chocolate_syrup: "Add chocolate syrup before the espresso for better mixing."
    };
    
    return hints[expectedStep] || "Follow the traditional barista sequence.";
  }

  getAccuracy() {
    const totalSteps = this.recipe.sequence.length;
    const correctSteps = this.completedSteps.filter(step => step.correct).length;
    const errorPenalty = this.errors.length * 0.1;
    
    return Math.max(0, (correctSteps / totalSteps) - errorPenalty);
  }

  getTimingScore() {
    if (this.stepTimes.length === 0) return 1;
    
    const expectedTimes = this.recipe.timing || [];
    let timingScore = 1;
    
    this.stepTimes.forEach((actualTime, index) => {
      if (expectedTimes[index]) {
        const expectedTime = expectedTimes[index];
        const timeDiff = Math.abs(actualTime - expectedTime);
        const tolerance = expectedTime * 0.3; // 30% tolerance
        
        if (timeDiff > tolerance) {
          timingScore -= 0.1;
        }
      }
    });
    
    return Math.max(0, timingScore);
  }

  getFinalScore() {
    const accuracy = this.getAccuracy();
    const timing = this.getTimingScore();
    const speedBonus = this.getTotalTime() < 30000 ? 0.1 : 0; // Bonus for under 30 seconds
    
    return Math.min(1, accuracy * 0.7 + timing * 0.2 + speedBonus + 0.1);
  }

  getTotalTime() {
    return Date.now() - this.startTime;
  }

  getDetailedFeedback() {
    const accuracy = this.getAccuracy();
    const timing = this.getTimingScore();
    const totalTime = this.getTotalTime();
    
    let feedback = {
      score: this.getFinalScore(),
      accuracy: accuracy,
      timing: timing,
      totalTime: totalTime,
      errors: this.errors.length,
      tips: []
    };
    
    // Generate specific tips
    if (accuracy < 0.8) {
      feedback.tips.push("Practice the sequence - each step builds on the previous one.");
    }
    
    if (timing < 0.7) {
      feedback.tips.push("Work on your timing - some steps need more time than others.");
    }
    
    if (this.errors.length > 2) {
      feedback.tips.push("Take your time and follow the recipe exactly.");
    }
    
    if (totalTime > 45000) {
      feedback.tips.push("Try to work faster - customers don't like to wait!");
    }
    
    if (feedback.score >= 0.9) {
      feedback.tips.push("Excellent work! You're a natural barista!");
    }
    
    return feedback;
  }

  reset() {
    this.currentStep = 0;
    this.completedSteps = [];
    this.errors = [];
    this.startTime = Date.now();
    this.stepTimes = [];
  }
}